import { useCallback, useEffect, useRef, useState } from "react";
import type { ComponentType } from "react";
import Auth from "../../Auth";
import CopyToClipboardButton from "../CopyToClipboardButton";
import { SudoButton } from "../../sudokit/components/SudoButton";
import { Text } from "../../sudokit/components/Text";
import { WithReset } from "../../sudokit/Layout/BoxReset";
import { SudoAccordion, SudoAccordionContent, SudoAccordionItem, SudoAccordionTrigger } from "../../sudokit/components/SudoAccordion";

type ChatSummary = {
  id: string;
  title: string | null;
  projectId: string;
  projectTitle: string | null;
  messageCount: number;
  assignedRuntime?: string | null;
  effectiveRuntime?: string | null;
  experimentId?: string | null;
  lastModel: string | null;
  agentRuntime?: string | null;
  lastMode: "write" | "chat" | null;
  toolCount: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  firstMessagePreview: string | null;
};

type PaginationInfo = {
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
};

type ChatPreferences = {
  tone: "friendly" | "straightforward" | "professional";
  responseLength: "concise" | "balanced" | "detailed";
  memoryEnabled: boolean;
  customInstructions: string;
};

type ChatMemory = {
  id: string;
  content: string;
  source: string | null;
  createdAt: string;
  updatedAt: string;
};

type Props = {
  userId: string;
  onSelectChat: (chatId: string) => void;
};

export function AgenticChatList({ userId, onSelectChat }: Props) {
  const TypedSudoAccordion = SudoAccordion as ComponentType<any>;
  const TypedSudoAccordionItem = SudoAccordionItem as ComponentType<any>;
  const TypedSudoAccordionTrigger = SudoAccordionTrigger as ComponentType<any>;
  const TypedSudoAccordionContent = SudoAccordionContent as ComponentType<any>;

  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [chatPreferences, setChatPreferences] = useState<ChatPreferences | null>(null);
  const [chatMemories, setChatMemories] = useState<ChatMemory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copyingChatId, setCopyingChatId] = useState<string | null>(null);
  const [copiedChatId, setCopiedChatId] = useState<string | null>(null);
  const copiedResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copiedResetTimeoutRef.current != null) {
        clearTimeout(copiedResetTimeoutRef.current);
      }
    };
  }, []);

  const fetchChats = useCallback(
    async (page: number = 1) => {
      setLoading(true);
      setError(null);
      try {
        const response = await Auth.fetchWithIdToken(`/api/admin/agentic-chats/${userId}`, {
          page,
          limit: 25,
        });
        if (response.success) {
          setChatPreferences(response.chatPreferences ?? null);
          setChatMemories(response.chatMemories ?? []);
          if (page === 1) {
            setChats(response.chats);
          } else {
            setChats((prev) => [...prev, ...response.chats]);
          }
          setPagination(response.pagination);
        } else {
          setError("Failed to fetch chats");
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to fetch chats");
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    fetchChats(1);
  }, [fetchChats]);

  const copyTextWithFallback = async (text: string) => {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.setAttribute("readonly", "");
    textArea.style.position = "absolute";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  };

  const handleCopyChatThreadJson = async (chatId: string) => {
    if (copiedResetTimeoutRef.current != null) {
      clearTimeout(copiedResetTimeoutRef.current);
      copiedResetTimeoutRef.current = null;
    }
    setCopyingChatId(chatId);
    setCopiedChatId(null);
    setError(null);

    try {
      const response = await Auth.fetchWithIdToken(`/api/admin/agentic-chats/${userId}/${chatId}`);
      if (!response?.success || !response?.chat) {
        throw new Error("Failed to fetch chat thread");
      }

      await copyTextWithFallback(JSON.stringify(response.chat, null, 2));
      setCopiedChatId(chatId);
      copiedResetTimeoutRef.current = setTimeout(() => {
        copiedResetTimeoutRef.current = null;
        setCopiedChatId((prev) => (prev === chatId ? null : prev));
      }, 2000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to copy chat thread JSON");
    } finally {
      setCopyingChatId((prev) => (prev === chatId ? null : prev));
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const toneLabel = chatPreferences?.tone ? chatPreferences.tone[0].toUpperCase() + chatPreferences.tone.slice(1) : "-";
  const responseLengthLabel = chatPreferences?.responseLength
    ? chatPreferences.responseLength[0].toUpperCase() + chatPreferences.responseLength.slice(1)
    : "-";

  if (error) {
    return (
      <WithReset className="p-6">
        <Text color="danger-color" size="sm" align="left">{error}</Text>
      </WithReset>
    );
  }

  if (loading && chats.length === 0) {
    return (
      <WithReset className="p-6">
        <Text color="grey-1" size="sm" align="left">Loading chats...</Text>
      </WithReset>
    );
  }

  if (chats.length === 0) {
    return (
      <WithReset className="p-6">
        <Text color="grey-1" size="sm" align="left">No agentic chats found for this user.</Text>
      </WithReset>
    );
  }

  return (
    <WithReset className="p-6 space-y-4">
      {chatPreferences && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <Text size="sm" weight="semibold" color="grey-0" align="left">Chat Preferences</Text>
          <div className="mt-3 grid gap-2 md:grid-cols-2 text-left">
            <Text size="sm" color="grey-1" align="left">
              <span className="font-medium text-gray-800">Tone:</span> {toneLabel}
            </Text>
            <Text size="sm" color="grey-1" align="left">
              <span className="font-medium text-gray-800">Response Length:</span> {responseLengthLabel}
            </Text>
            <Text size="sm" color="grey-1" align="left">
              <span className="font-medium text-gray-800">Memory:</span> {chatPreferences.memoryEnabled ? "On" : "Off"}
            </Text>
            <Text size="sm" color="grey-1" align="left" className="break-words">
              <span className="font-medium text-gray-800">Custom Instructions:</span>{" "}
              {chatPreferences.customInstructions?.trim() ? chatPreferences.customInstructions : "None"}
            </Text>
          </div>

          <div className="mt-3">
            <TypedSudoAccordion
              type="single"
              collapsible
            >
              <TypedSudoAccordionItem value="chat-memories">
                <TypedSudoAccordionTrigger className="px-3 py-2 text-sm font-medium hover:bg-gray-100 rounded-md">
                  <span className="text-gray-800">Memories ({chatMemories.length})</span>
                </TypedSudoAccordionTrigger>
                <TypedSudoAccordionContent className="px-3 pt-2">
                  {chatMemories.length === 0 ? (
                    <Text size="sm" color="grey-1" align="left">No memories saved.</Text>
                  ) : (
                    <ul className="space-y-2">
                      {chatMemories.map((memory) => (
                        <li
                          key={memory.id}
                          className="rounded border border-gray-200 bg-white p-2"
                        >
                          <Text size="sm" color="grey-0" align="left" className="break-words">{memory.content}</Text>
                        </li>
                      ))}
                    </ul>
                  )}
                </TypedSudoAccordionContent>
              </TypedSudoAccordionItem>
            </TypedSudoAccordion>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse bg-white border border-gray-200 rounded-lg text-left">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left p-3 text-sm font-medium text-gray-700 border-b border-gray-200">Title / Preview</th>
              <th className="text-left p-3 text-sm font-medium text-gray-700 border-b border-gray-200 w-[200px] max-w-[200px]">Project</th>
              <th className="text-left p-3 text-sm font-medium text-gray-700 border-b border-gray-200 w-[200px] max-w-[200px]">Chat ID</th>
              <th className="text-left p-3 text-sm font-medium text-gray-700 border-b border-gray-200 whitespace-nowrap">Messages</th>
              <th className="text-left p-3 text-sm font-medium text-gray-700 border-b border-gray-200">Runtime / Model</th>
              <th className="text-left p-3 text-sm font-medium text-gray-700 border-b border-gray-200">Mode</th>
              <th className="text-left p-3 text-sm font-medium text-gray-700 border-b border-gray-200">Updated</th>
              <th className="text-left p-3 text-sm font-medium text-gray-700 border-b border-gray-200">Actions</th>
            </tr>
          </thead>
          <tbody>
            {chats.map((chat) => (
              <tr
                key={chat.id}
                onClick={() => onSelectChat(chat.id)}
                className="hover:bg-gray-50 cursor-pointer"
              >
                <td className="p-3 text-sm text-gray-900 border-b border-gray-100 max-w-xs text-left">
                  <div className="flex flex-col gap-1 text-left">
                    <Text
                      size="sm"
                      color="grey-0"
                      align="left"
                      truncate
                      className={chat.deletedAt ? "line-through opacity-50" : ""}
                    >
                      {chat.title || chat.firstMessagePreview || "Untitled"}
                    </Text>
                    {chat.deletedAt && (
                      <Text size="xs" color="danger-color" weight="medium" align="left">Deleted</Text>
                    )}
                  </div>
                </td>
                <td className="p-3 text-sm text-gray-600 border-b border-gray-100 text-left w-[200px] max-w-[200px]">
                  <div className="min-w-0 space-y-1">
                    {chat.projectTitle ? (
                      <Text
                        size="sm"
                        color="grey-1"
                        align="left"
                        truncate
                        className="max-w-full block"
                        title={chat.projectTitle}
                      >
                        {chat.projectTitle}
                      </Text>
                    ) : null}
                    <div className="flex min-w-0 items-center gap-1">
                      <span
                        className="min-w-0 flex-1 truncate font-mono text-xs text-gray-700"
                        title={chat.projectId}
                      >
                        {chat.projectId}
                      </span>
                      <div className="shrink-0" onClick={(event) => event.stopPropagation()}>
                        <CopyToClipboardButton
                          useIcon
                          textToCopy={chat.projectId}
                          displayText="Copy project ID"
                          tooltipText="Copy project ID"
                          successMessage="Project ID copied"
                          className="!p-0.5"
                          iconClassName="!w-4 !h-4"
                        />
                      </div>
                    </div>
                  </div>
                </td>
                <td className="p-3 text-sm text-gray-600 border-b border-gray-100 text-left w-[200px] max-w-[200px]">
                  <div className="flex min-w-0 items-center gap-1">
                    <span
                      className="min-w-0 flex-1 truncate font-mono text-xs text-gray-700"
                      title={chat.id}
                    >
                      {chat.id}
                    </span>
                    <div className="shrink-0" onClick={(event) => event.stopPropagation()}>
                      <CopyToClipboardButton
                        useIcon
                        textToCopy={chat.id}
                        displayText="Copy chat ID"
                        tooltipText="Copy chat ID"
                        successMessage="Chat ID copied"
                        className="!p-0.5"
                        iconClassName="!w-4 !h-4"
                      />
                    </div>
                  </div>
                </td>
                <td className="p-3 text-sm text-gray-900 border-b border-gray-100 text-left whitespace-nowrap">
                  <Text size="sm" color="grey-0" align="left">{chat.messageCount}</Text>
                </td>
                <td className="p-3 text-sm border-b border-gray-100 text-left">
                  {/* Show the effective runtime while retaining the original assignment after rollback. */}
                  <div className="mb-2 font-mono text-xs" title={chat.experimentId || "No saved assignment"}>
                    Runtime: {chat.effectiveRuntime ?? chat.agentRuntime ?? "Unassigned"}
                    {chat.assignedRuntime && chat.assignedRuntime !== chat.effectiveRuntime && ` (assigned ${chat.assignedRuntime})`}
                  </div>
                  {chat.lastModel ? (
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                      {chat.lastModel}
                    </span>
                  ) : (
                    <Text size="sm" color="grey-2" align="left">-</Text>
                  )}
                </td>
                <td className="p-3 text-sm border-b border-gray-100 text-left">
                  {chat.lastMode ? (
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      chat.lastMode === "write" ? "bg-green-50 text-green-700" : "bg-purple-50 text-purple-700"
                    }`}>
                      {chat.lastMode}
                    </span>
                  ) : (
                    <Text size="sm" color="grey-2" align="left">-</Text>
                  )}
                </td>
                <td className="p-3 text-sm text-gray-600 border-b border-gray-100 whitespace-nowrap text-left">
                  <Text size="sm" color="grey-1" align="left">{formatDate(chat.updatedAt)}</Text>
                </td>
                <td className="p-3 text-sm text-gray-600 border-b border-gray-100 whitespace-nowrap text-left">
                  <SudoButton
                    variant="tertiaryGrey"
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleCopyChatThreadJson(chat.id);
                    }}
                    disabled={copyingChatId === chat.id}
                    aria-label={`Copy JSON thread for chat ${chat.title || chat.id}`}
                  >
                    {copyingChatId === chat.id
                      ? "Copying..."
                      : copiedChatId === chat.id
                        ? "Copied!"
                        : "Copy Thread JSON"}
                  </SudoButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination && pagination.hasMore && (
        <div className="flex justify-center pt-2">
          <SudoButton
            onClick={() => fetchChats(pagination.page + 1)}
            disabled={loading}
            variant="secondary"
            size="small"
            state={loading ? "loading" : "default"}
          >
            {loading ? "Loading..." : `Load More (${pagination.total - chats.length} remaining)`}
          </SudoButton>
        </div>
      )}

      {pagination && (
        <Text size="xs" color="grey-2" align="center" className="pt-2">
          Showing {chats.length} of {pagination.total} chats
        </Text>
      )}
    </WithReset>
  );
}
